import { Box, CircularProgress, ClickAwayListener } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { CloseIcon } from 'assets/icons'
import SearchBarWithButton from 'components/inputs/SearchBarWithButton'
import {
  appUITypeToDocType,
  useAppUIType
} from 'components/providers/AppUITypeProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import EventGlobalsProvider from 'components/providers/EventGlobalsProvider'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import { ReactNode, useEffect, useState } from 'react'
import { searchPublicGroups } from 'services/api/group'
import ObserveService from 'services/db/Observe'
import { allCalendarItems } from 'services/db/Queries/Event'
import { allGroups, allGroupsForUI } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  EventDoc,
  EventStatus,
  GroupDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { getArchivedGroups } from 'utils/api/userSettings'
import { is24meApp } from 'utils/appType'
import { groupIdToProvider } from 'utils/groups'
import { generateGroupcalArrayFromRecurrence } from 'utils/recurrence'

import * as Styled from '../styled'
import { PopupCloseButton } from '../styled'
import EventsColumn from './EventsColumn'
import SearchTabs from './SearchTabs'

// Polyfill for requestIdleCallback (Safari compatibility)
const requestIdleCallbackPolyfill =
  typeof requestIdleCallback !== 'undefined'
    ? requestIdleCallback
    : (cb: IdleRequestCallback) => {
        const start = Date.now()
        return setTimeout(() => {
          cb({
            didTimeout: false,
            timeRemaining: () => Math.max(0, 50.0 - (Date.now() - start))
          } as IdleDeadline)
        }, 1)
      }

export enum SEARCH_MODE {
  OnlyEvents,
  OnlyAllEvents,
  All
}

export interface SearchPopupProps {
  children: ReactNode
  open: boolean
  setOpen: (b: boolean) => void
  userSettings?: Model<UserSettingsDoc>
  account?: Model<AccountDoc>
  mode?: SEARCH_MODE
  groupId?: string
  textfieldPlaceholder?: string
  events?: Model<EventDoc>[] | EventDoc[] | undefined
}

export function SearchPopup(props: SearchPopupProps) {
  const appUiTypeContext = useAppUIType()
  const calendarSync = useCalendarSync()
  const [events, setEvents] = useState<EventDoc[]>([])
  const [groups, setGroups] = useState<Model<GroupDoc>[]>([])
  const [groupsForEventContext, setGroupsForEventContext] = useState<
    Model<GroupDoc>[]
  >([])
  const [publicGroups, setPublicGroups] = useState<Model<GroupDoc>[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined)

  function clearAllData() {
    setEvents([])
    setGroups([])
    setGroupsForEventContext([])
    setPublicGroups([])
    setIsLoading(undefined)
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearAllData()

    setSearchValue(event.target.value)
  }

  const archivedGroups = getArchivedGroups(props.userSettings)

  const applyFilter = async (withClear: boolean = true) => {
    // Show loading state immediately
    if (withClear) {
      setEvents([])
      setGroups([])
      setGroupsForEventContext([])
      setIsLoading(undefined)
    }

    // Run filtering and processing asynchronously to avoid blocking UI
    const filterAsync = async () => {
      try {
        // Fetch all events first
        const allEvents = await allCalendarItems()

        // Process filtering in chunks to yield to browser
        const filtered = await new Promise<EventDoc[]>((resolve) => {
          const result: EventDoc[] = []
          let index = 0

          const processChunk = () => {
            const chunkSize = 100
            const end = Math.min(index + chunkSize, allEvents?.length ?? 0)

            for (let i = index; i < end; i++) {
              const event = allEvents?.[i]
              if (!event) continue

              // Text filter
              if (searchValue.length > 0) {
                const searchLower = searchValue.toLowerCase()
                const matches =
                  event.Text?.toLowerCase()?.includes(searchLower) ||
                  event.Location?.Address?.toLowerCase()?.includes(
                    searchLower
                  ) ||
                  (event.Notes &&
                    typeof event.Notes !== 'string' &&
                    event.Notes?.some((note) =>
                      note.Note.toLowerCase().includes(searchLower)
                    )) ||
                  (event.Location &&
                    typeof event.Location !== 'string' &&
                    event.Location?.Address?.toLowerCase()?.includes(
                      searchLower
                    ))

                if (!matches) continue
              }

              // Group filter
              if (
                (LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length ??
                  0) > 0 &&
                props.mode === SEARCH_MODE.OnlyEvents
              ) {
                if (
                  event.GroupID !==
                  LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
                )
                  continue
              }

              // Mode-specific filters
              if (props.mode === SEARCH_MODE.OnlyEvents && is24meApp()) {
                if (appUiTypeContext.selectedLabel) {
                  if (
                    event.Type !==
                      appUITypeToDocType(appUiTypeContext.uiType.value) ||
                    !event.Label ||
                    typeof event.Label === 'string' ||
                    !event.Label.some((label) => {
                      return (
                        label.LabelText === appUiTypeContext.selectedLabel?.ID
                      )
                    })
                  )
                    continue
                }

                if (
                  (LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length ??
                    0) > 0
                ) {
                  if (
                    event.Type !==
                      appUITypeToDocType(appUiTypeContext.uiType.value) ||
                    event.Recurrence
                  )
                    continue
                }
              }

              // Status filter
              if (
                event.Status === EventStatus.DELETED ||
                event.Status === EventStatus.REMOVED
              )
                continue

              // Archive filter
              if (props.mode !== SEARCH_MODE.All) {
                if (
                  props.mode === SEARCH_MODE.OnlyEvents &&
                  !LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
                ) {
                  if (archivedGroups.includes(event.GroupID ?? '')) continue
                } else {
                  if (
                    event.GroupID !==
                      LocalStorage.get(LocalStorageKey.SELECTED_GROUP) &&
                    archivedGroups.includes(event.GroupID ?? '')
                  )
                    continue
                }
              }

              result.push(event)
            }

            index = end

            if (index < (allEvents?.length ?? 0)) {
              // Yield to browser before processing next chunk
              requestIdleCallbackPolyfill(processChunk, { timeout: 1000 })
            } else {
              resolve(result)
            }
          }

          processChunk()
        })

        // Sort the filtered events
        const sortedEvents = filtered.sort((a, b) => {
          if (a.StartDate === b.StartDate) {
            return Number(b.EndDate) - Number(a.EndDate)
          }
          return Number(b.StartDate) - Number(a.StartDate)
        })

        // Process non-recurring events first
        const eventsForUI: EventDoc[] = sortedEvents.filter(
          (event) => !event.Recurrence || typeof event.Recurrence === 'string'
        )

        // Chunk and set events progressively and await completion
        const chunkSize = 100
        setEvents([])

        await new Promise<void>((resolveChunks) => {
          if (eventsForUI.length === 0) return resolveChunks()

          let chunksPosted = 0
          const totalChunks = Math.ceil(eventsForUI.length / chunkSize)

          for (let i = 0; i < eventsForUI.length; i += chunkSize) {
            const chunk = eventsForUI.slice(i, i + chunkSize)
            setTimeout(
              () => {
                setEvents((prev) => [...prev, ...chunk])
                chunksPosted++
                if (chunksPosted >= totalChunks) resolveChunks()
              },
              (i / chunkSize) * 16
            ) // Spread across frames
          }
        })

        // Process recurring events asynchronously in background
        const recurringEvents = sortedEvents.filter(
          (event) => event.Recurrence && typeof event.Recurrence !== 'string'
        )

        // Expand and add recurring events, await completion
        await new Promise<void>((resolveRecurring) => {
          if (recurringEvents.length === 0) return resolveRecurring()

          setTimeout(() => {
            const expandedRecurring: EventDoc[] = []
            let processed = 0

            const processRecurring = () => {
              const batchSize = 10
              const end = Math.min(
                processed + batchSize,
                recurringEvents.length
              )

              for (let i = processed; i < end; i++) {
                expandedRecurring.push(
                  ...generateGroupcalArrayFromRecurrence(
                    recurringEvents[i],
                    false,
                    dayjs().subtract(20, 'year'),
                    dayjs().add(1, 'year'),
                    getDefaultTimezone()
                  )
                )
              }

              processed = end

              if (processed < recurringEvents.length) {
                requestIdleCallbackPolyfill(processRecurring, { timeout: 1000 })
              } else {
                // Add expanded recurring events to the list in chunks and resolve after last chunk
                const chunkSize2 = 100
                if (expandedRecurring.length === 0) return resolveRecurring()

                let posted = 0
                const total = Math.ceil(expandedRecurring.length / chunkSize2)
                for (let i = 0; i < expandedRecurring.length; i += chunkSize2) {
                  const chunk = expandedRecurring.slice(i, i + chunkSize2)
                  setTimeout(
                    () => {
                      setEvents((prev) => [...prev, ...chunk])
                      posted++
                      if (posted >= total) resolveRecurring()
                    },
                    (i / chunkSize2) * 16
                  )
                }
              }
            }

            processRecurring()
          }, 0)
        })
      } catch (error) {
        console.error('Error filtering events:', error)
        // Ensure we don't hang the loader
        throw error
      }
    }

    // Start async processing and wait for both events and groups
    try {
      const groupPromise = allGroups().then((loadedGroups) => {
        setGroupsForEventContext(loadedGroups ?? [])

        const filteredGroups = loadedGroups?.filter((group) => {
          if (searchValue.length === 0) return true
          return group.Name?.toLowerCase()?.includes(searchValue.toLowerCase())
        })

        setGroups(filteredGroups ?? [])
      })

      await Promise.all([filterAsync(), groupPromise])
    } finally {
      setIsLoading(false)
    }
  }

  let placeholder = ''

  if (props.textfieldPlaceholder) {
    placeholder = props.textfieldPlaceholder
  }

  useEffect(() => {
    if (props.open) {
      setSearchValue('')
      clearAllData()
    }
  }, [props.open])

  useEffect(() => {
    if (searchValue.length !== 0) {
      applyFilter(false)
    }
  }, [props.events])

  return (
    <Styled.SearchPopup
      open={props.open}
      placement="left"
      fallbackPlacements={['right']}
      closeOnTouchOutside={false}
      onClickAwayAction={() => {
        // do nothing
      }}
      content={
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={() => {
            if (LocalStorage.get(LocalStorageKey.VISIBLE_EVENT) === null) {
              props.setOpen(false)
              clearAllData()
            }
          }}
        >
          <Box
            sx={{
              minWidth: props.mode === SEARCH_MODE.All ? '1050px' : '550px',
              width: props.mode === SEARCH_MODE.All ? '1050px' : '550px',
              height: props.mode !== SEARCH_MODE.All ? '750px' : undefined
            }}
            position={'relative'}
            display={'flex'}
            flexDirection={'column'}
            alignContent={'center'}
            gap={2}
          >
            {isLoading && (
              <Box
                position={'absolute'}
                top={0}
                left={0}
                right={0}
                bottom={0}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                sx={{ backgroundColor: 'transparent', zIndex: 5 }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box
              width={'100%'}
              display={'flex'}
              flexDirection={'row-reverse'}
              paddingLeft={4}
              paddingRight={4}
            >
              <GroupcalTooltip title={t('close')}>
                <PopupCloseButton
                  type={'button'}
                  id="close-search-button"
                  size="sm"
                  onClick={() => {
                    props.setOpen(false)
                    clearAllData()
                  }}
                >
                  <CloseIcon />
                </PopupCloseButton>
              </GroupcalTooltip>
            </Box>
            <SearchBarWithButton
              placeholder={placeholder}
              onSearch={() => {
                clearAllData()
                setIsLoading(true)
                if (props.mode === SEARCH_MODE.All) {
                  searchPublicGroups(searchValue)
                    .then((data) => {
                      setPublicGroups(
                        data['data'].map((item: any) => {
                          const group: GroupDoc = { ...item }
                          return group
                        })
                      )
                    })
                    .catch((error) => {
                      Logger.red(
                        'SEARCH POPUP',
                        'Error searching public groups:',
                        error
                      )
                    })

                  calendarSync.sync(undefined, searchValue).then(() => {
                    applyFilter()
                  })
                } else {
                  const provider = groupIdToProvider(props.groupId ?? '')

                  Logger.orange(
                    'SEARCH POPUP',
                    'Searching calendars for provider:',
                    provider
                  )

                  if (provider) {
                    calendarSync.sync(provider, searchValue).then(() => {
                      applyFilter()
                    })
                  } else {
                    applyFilter()
                  }
                }
              }}
              onTextChange={handleFilterChange}
              searchValue={searchValue}
            />
            <EventGlobalsProvider
              account={props.account}
              userSettings={props.userSettings}
              groups={groupsForEventContext}
            >
              {props.mode === SEARCH_MODE.OnlyEvents ||
              props.mode === SEARCH_MODE.OnlyAllEvents ? (
                <EventsColumn events={events} showTime loading={isLoading} />
              ) : (
                <SearchTabs
                  account={props.account}
                  userSettings={props.userSettings}
                  events={events}
                  groups={allGroupsForUI(
                    groups ?? [],
                    props.userSettings!,
                    true
                  )}
                  setOpen={props.setOpen}
                  publicGroups={publicGroups}
                  loading={isLoading}
                />
              )}
            </EventGlobalsProvider>
          </Box>
        </ClickAwayListener>
      }
    >
      {props.children}
    </Styled.SearchPopup>
  )
}

export default withObservables([], (props: SearchPopupProps) => ({
  userSettings: ObserveService.UserSettings(),
  account: ObserveService.Account(),
  events: ObserveService.Events()
}))(SearchPopup)
